"use client";

import React from "react";
import {
  useCompareTrayStore,
  useIsCompareSelected
} from "../../molecules/compare-tray/compare-tray-context.client";
import { analyticsText, fireLinkClick, firePageView } from "../../services/analytics";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The checkbox leaf for a plan card's "Compare" row.
 *
 * ⚠ Context-aware by design. When rendered inside a `CompareTrayProvider`
 * (which a carousel wraps when `compare_enabled` is on), the
 * checkbox is CONTROLLED by the store: the `checked` state and the toggle
 * route through `useIsCompareSelected` + `useCompareTrayStore`, so this card
 * and the Compare Tray stay in lock-step with every other card in the row.
 *
 * Outside a provider the checkbox is UNCONTROLLED — the stored card from
 * before the integration existed still works unchanged, which is why
 * `defaultChecked` is preserved as a fallback.
 *
 * ⚠ A handler prop was ruled out at the author side on purpose: the DOM-event
 * exception in the DDS CLAUDE.md "handler prop has to earn itself" tests
 * forbids an `onChange` surface here. The context pattern carries selection
 * data OUT of the control (test 2) without passing a function IN (tests 1,3).
 */

export interface PlanCardCompareInputProps {
  /** Shared across the compare group — same `name` on every card in the row. */
  name?: string;
  /** This card's identity in the compare group. */
  value?: string;
  /** Initial checked state when there is no provider — ignored under one. */
  defaultChecked?: boolean;
  /** The card's package name as displayed (e.g. "CHOICE™"), for analytics. */
  packageName?: string;
  className?: string;
}

/* The tray's virtual path (WAT-12612): the current page URL plus the modal
   name, e.g. `https://stage.directv.com/stream/welcome/comparison_tray`. Built
   as the Modal molecule builds its own — origin + path, trailing slash
   dropped, query string left out. Shared by the click and the page view so the
   two cannot drift apart; only ever called from an event handler, so
   `location` is always there. */
const COMPARE_TRAY_MODAL_NAME = "comparison_tray";

function compareTrayDestination(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname.replace(/\/+$/, "")}/${COMPARE_TRAY_MODAL_NAME}`;
}

/* The package name as analytics reports it (WAT-12612): lower case, with the
   trademark marks dropped — "CHOICE™" reports as "choice". Accents stay, so
   "ÓPTIMO MÁS™" reports as "óptimo más". */
const reportedPackageName = (raw: string | undefined): string => analyticsText((raw || "").replace(/[™®℠]/g, ""));

/* ⚠ Normalise once at the boundary so a card authored as `"Choice"` and a
   tray chip authored as `"choice"` keep lock-step in the store. Mirrors
   `handleCompareClick`'s `.toLowerCase()` of selections before the URL
   build — without it a mixed-case author produces two store entries for the
   same package. */
const normalizeTag = (raw: string | undefined): string => (typeof raw === "string" ? raw.trim().toLowerCase() : "");

function PlanCardCompareInputImpl({
  name,
  value,
  defaultChecked,
  packageName,
  className
}: PlanCardCompareInputProps) {
  const store = useCompareTrayStore();
  const normalized = normalizeTag(value);
  const selected = useIsCompareSelected(normalized);

  /* ⚠ `defaultChecked` is honoured only outside a provider. Pre-ticked cards
     under a provider seed through the carousel's `initialSelected` prop so
     the first server render already carries the selection — no post-hydrate
     flip, no `MAX_COMPARE_LIMIT` beacon fired on page load when more
     defaultChecked cards exist than the cap allows. */

  if (!store) {
    /* No provider — stay uncontrolled so a standalone card renders the same
       checkbox it did before the Compare Tray existed. */
    return (
      <input
        className={className}
        defaultChecked={defaultChecked}
        name={name}
        type="checkbox"
        value={value}
      />
    );
  }

  /* With a provider: controlled. The store rejects a fourth selection and
     fires the max-limit beacon on its own, so the handler is a thin shim. */
  return (
    <input
      checked={normalized ? selected : false}
      className={className}
      disabled={!normalized}
      name={name}
      onChange={event => {
        if (!normalized) {
          return;
        }
        /* Record the control that caused the open so the tray can restore
           focus here when it closes / collapses. */
        store.triggerElementRef.current = event.currentTarget;
        /* Ported from the source handler (`package-feature.tsx:78-93`): a
           tick expands the tray, an untick collapses it to the bar, and the
           tray always opens. Read before the update so it reflects the click. */
        const { trayOpen, selectedItems } = store.getState();
        const isUntick = selectedItems.includes(normalized);
        store.updateSelectedTag(normalized);
        store.updateCollapsedTray(!isUntick);
        store.setTrayOpen(true);
        /* WAT-12612: a card without a package name reports the bare prefix
           rather than a dangling underscore. */
        const linkNamePrefix = isUntick ? "compare checkbox unselect" : "compare checkbox";
        const reportedName = reportedPackageName(packageName);
        const destination = compareTrayDestination();
        fireLinkClick({
          linkName: reportedName ? `${linkNamePrefix}_${reportedName}` : linkNamePrefix,
          linkPosition: "panel_plan card",
          linkDestination: destination
        });
        /* The tray opening reports as a modal page view, which replaces the
           retired `modal_status` event. */
        if (!trayOpen) {
          firePageView({
            linkPosition: "modal_comparison tray",
            linkDestination: destination
          });
        }
      }}
      type="checkbox"
      value={value}
    />
  );
}
export const PlanCardCompareInput = withSafeRender(PlanCardCompareInputImpl, "PlanCardCompareInput");
